/**
 * @fileoverview DownloadStep Component - File Download Management
 *
 * This component handles the download step (Step 3) of the BlurMe workflow.
 * It displays processed files and provides download options for individual
 * files or bulk download of all files.
 *
 * @description
 * Key features:
 * - Individual file download buttons
 * - Download all files option with sequential downloads
 * - Completion state tracking and visual feedback
 * - Reset functionality to process new images
 *
 * @requires React
 */
import React from 'react';
import DonateSection from './DonateSection';

/**
 * DownloadStep Component
 *
 * @param {Object} props
 * @param {Array} props.files - Array of processed file objects
 * @param {number} props.downloadCount - Number of files already downloaded
 * @param {Function} props.onDownload - Callback to download a single file
 * @param {Function} props.onDownloadAll - Callback to download all files
 * @param {Function} props.onReset - Callback to reset and start over
 * @param {boolean} props.allDownloaded - Whether all files have been downloaded
 * @param {boolean} props.isVideoMode - Whether processing videos
 * @returns {JSX.Element}
 */
function DownloadStep({ files, downloadCount, onDownload, onDownloadAll, onReset, allDownloaded, isVideoMode = false }) {
  /* ========================================================================
     Derived State
     ======================================================================== */

  const total = files.length;
  const isComplete = downloadCount >= total && total > 0;

  /* ========================================================================
     Render
     ======================================================================== */

  return (
    <div className="max-w-2xl mx-auto text-center">
      {/* ================================================================
          Status Header
          - Dynamic icon (download or checkmark)
          - Completion message
          ================================================================ */}
      <div className="mb-4">
        <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${
          isComplete ? 'bg-green-500/30' : 'bg-emerald-500/20'
        }`} style={{ boxShadow: `0 0 40px rgba(${isComplete ? '34, 197, 94' : '16, 185, 129'}, 0.3)` }}>
          <svg className={`w-8 h-8 ${isComplete ? 'text-green-400' : 'text-emerald-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isComplete ? 'M5 13l4 4L19 7' : 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'}
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold">{isComplete ? 'Download Complete!' : 'Ready to Download'}</h2>
        <p className="text-gray-400 text-sm mt-1">
          {isComplete ? 'All files saved to Downloads folder' : `Download your blurred ${isVideoMode ? 'videos' : 'images'}`}
        </p>
      </div>

      {/* ================================================================
          File Download List
          - Individual file cards with download buttons
          - Download all button
          ================================================================ */}
      <div className="bg-white/5 backdrop-blur rounded-xl p-4 mb-4">
        {/* Individual File Cards */}
        {files.map((file, i) => {
          const blurCount = Object.values(file.blurSettings || {}).filter(s => s?.enabled).length + (file.customBlurBoxes || []).filter(b => b?.enabled).length;
          const isVideo = file.isVideo || isVideoMode;
          const outputExt = isVideo ? 'mp4' : 'png';
          const outputName = `${file.name.replace(/\.[^.]+$/, '')}_blurred.${outputExt}`;

          return (
            <div key={i} className="p-3 bg-white/5 rounded-lg mb-2">
              <div className="flex items-center gap-3 mb-2">
                {isVideo ? (
                  <div className="relative w-10 h-10">
                    <img
                      src={file.thumbnail || file.preview}
                      alt=""
                      className="w-10 h-10 rounded object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 bg-black/50 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img src={file.preview} alt="" className="w-10 h-10 rounded object-cover" />
                )}
                <div className="text-left flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{outputName}</p>
                  <p className="text-xs text-gray-400">
                    {blurCount} {isVideo ? 'person' : 'region'}{blurCount !== 1 ? 's' : ''} blurred
                    {isVideo && (() => {
                      // Use trimmed duration if available, otherwise full duration
                      const duration = file.trimRange
                        ? (file.trimRange.end - file.trimRange.start)
                        : file.metadata?.duration_seconds;
                      if (!duration) return null;
                      return <> • {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</>;
                    })()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onDownload(i)}
                disabled={isVideo && !file.downloadUrl}
                className="w-full py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
              >
                {isVideo && !file.downloadUrl ? 'Processing...' : 'Download'}
              </button>
            </div>
          );
        })}

        {/* Download All Button */}
        <button
          onClick={onDownloadAll}
          className="w-full mt-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-xl font-semibold transition-colors"
        >
          Download All ({total} file{total !== 1 ? 's' : ''})
        </button>

        <p className="text-xs text-gray-500 mt-3">Files save to your browser's Downloads folder</p>
      </div>

      {/* Support/Donate Section */}
      <DonateSection className="mb-4" />

      {/* ================================================================
          Reset Button
          - Start over with new images/videos
          ================================================================ */}
      <button
        onClick={onReset}
        className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
      >
        🔄 Process More {isVideoMode ? 'Videos' : 'Images'}
      </button>
    </div>
  );
}

export default DownloadStep;
